import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import EnquiryModal from "./EnquiryModal";

const IMAGES = {
  junior: "https://i.postimg.cc/6q0FnsNP/MG-1727-Pano.jpg",
  senior: "https://i.postimg.cc/RhcDmkFW/DJI-0474-MP4-00-00-10-04-Still001-1.jpg",
  juniorIcons: [
    {
      title: "Innovative Learning",
      description: "Learners engage in real-world projects that encourage exploration, discovery, and problem-solving.",
      icon: "https://i.postimg.cc/qvbVVWBb/business-4271251-1280.png",
    },
    {
      title: "Cultural Experiences",
      description: "Art, drama, and cultural studies expand perspectives and foster empathy and global citizenship.",
      icon: "https://i.postimg.cc/Jn9RwvxH/heart-7039299-1280.png",
    },
    {
      title: "Sports & PE",
      description: "Athletic programs emphasize teamwork, leadership, and resilience through skill-based training.",
      icon: "https://i.postimg.cc/7hDQTNYC/ball-305828-1280.png",
    },
    {
      title: "Exploratory Classes",
      description: "Cross-disciplinary courses in STEM, arts, and design empower learners to identify and develop their talents.",
      icon: "https://i.postimg.cc/636v22xV/geometry-1698595-1280.png",
    },
  ],
  seniorIcons: [
    {
      title: "Academic Excellence",
      description: "Students master core competencies through inquiry-based learning and individualized mentorship.",
      icon: "https://i.postimg.cc/6pNf0fkD/books-41930-1280.png",
    },
    {
      title: "Extracurricular",
      description: "Leadership programs, clubs, and outreach opportunities nurture holistic growth and civic engagement.",
      icon: "https://i.postimg.cc/wMpjP39M/podium-1060918-1280.png",
    },
    {
      title: "Experienced Faculty",
      description: "Our teachers mentor each learner, guiding them toward mastery and self-driven academic excellence.",
      icon: "https://i.postimg.cc/prJLtxdz/student-8851356-1280.png",
    },
    {
      title: "Christian Community",
      description: "Faith-based values inspire service, moral integrity, and community transformation.",
      icon: "https://i.postimg.cc/tR3T6dzg/priest-36356-1280.png",
    },
  ],
};

// --------------------
// StepBlock Component
// --------------------
const StepBlock = ({ step, index }) => {
  const isEven = index % 2 === 0;
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });

  const textVariant = {
    hidden: { opacity: 0, x: isEven ? 50 : -50 },
    visible: { opacity: 1, x: 0 },
  };

  const iconVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const imageOrder = isEven ? "md:order-1" : "md:order-2";
  const textOrder = isEven ? "md:order-2" : "md:order-1";

  const hoverEffect = { scale: 1.05, boxShadow: "0 10px 20px rgba(0,0,0,0.2)" };

  return (
    <section className="flex flex-col md:flex-row items-stretch md:min-h-[90vh] mb-12">
      {/* Image */}
      <motion.div
        ref={ref}
        className={`md:w-1/2 h-64 md:h-auto ${imageOrder} md:sticky top-0`}
        whileHover={{ scale: 1.02 }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.6 }}
      >
        <img
          src={step.image}
          alt={step.title}
          loading="lazy"
          className="w-full h-full object-cover rounded-2xl shadow-lg"
        />
      </motion.div>

      {/* Text */}
      <motion.div
        className={`md:w-1/2 p-6 md:p-8 flex flex-col justify-start bg-white dark:bg-slate-900 space-y-6 ${textOrder}`}
        variants={textVariant}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-primary dark:text-yellow-400">
          {step.title}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">{step.intro}</p>
        <p className="font-semibold text-gray-800 dark:text-gray-200">{step.grades}</p>

        {/* Sections */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {step.sections.map((sec, i) => (
            <motion.div
              key={i}
              variants={iconVariant}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              whileHover={hoverEffect}
              className="flex flex-col items-center p-3 rounded-xl shadow-sm bg-primary/10 dark:bg-primary/20 text-center"
            >
              <img src={sec.icon} alt={`${sec.title} icon`} className="w-12 h-12 mb-2" />
              <h3 className="font-semibold text-primary dark:text-yellow-400">{sec.title}</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">{sec.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

// --------------------
// Admissions Page
// --------------------
const Admissions = () => {
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { amount: 0.3 });
  const buttonRef = useRef(null);
  const buttonInView = useInView(buttonRef, { amount: 0.3 });

  const steps = [
    {
      title: "Junior School",
      image: IMAGES.junior,
      intro: "Junior School nurtures creativity, curiosity, and holistic development using the CBE methodology.",
      grades: "Grades 7–9",
      sections: IMAGES.juniorIcons,
    },
    {
      title: "Senior School",
      image: IMAGES.senior,
      intro: "Senior School prepares students to excel in academics, leadership, and character, guided by competency-based education principles.",
      grades: "Grades 10–12",
      sections: IMAGES.seniorIcons,
    },
  ];

  const buttonHover = { scale: 1.03 };

  return (
    <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white overflow-hidden">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={headerInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-4 text-primary dark:text-yellow-400">
            Admissions Overview
          </h1>
          <p className="max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
            Welcome to Anestar Victory Junior & Senior School. Our competency-based admissions
            process ensures students thrive academically, socially, and spiritually.
          </p>
        </motion.div>

        {/* Fee & Enquiry Buttons */}
        <motion.div
          ref={buttonRef}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={buttonInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-12"
        >
          <a
            href="/JUNIOR-SCHOOL-FEES.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto px-5 py-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white rounded-md hover:brightness-110 transition duration-300"
          >
            Junior School Fees
          </a>
          <motion.button
            whileHover={buttonHover}
            onClick={() => setIsEnquiryOpen(true)}
            aria-label="Open enquiry form"
            className="w-full sm:w-auto px-5 py-2 bg-slate-800 dark:bg-slate-700 text-white rounded-md hover:bg-slate-900 transition duration-300"
          >
            Enquiry Form
          </motion.button>
          <a
            href="/SENIOR-SCHOOL-FEES.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto px-5 py-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white rounded-md hover:brightness-110 transition duration-300"
          >
            Senior School Fees
          </a>
        </motion.div>

        {/* School Levels */}
        {steps.map((step, index) => (
          <StepBlock key={index} step={step} index={index} />
        ))}
      </div>

      <EnquiryModal isOpen={isEnquiryOpen} onClose={() => setIsEnquiryOpen(false)} />
    </div>
  );
};

export default Admissions;
